/**
 * 65.30 (decision 179): a round's flight under drag — quadratic, dv/dt = −c·|v|·v, c = ln 2 / (its cartridge's
 * half-speed distance), so its speed halves every that far it flies (v(x) = v₀·e^(−c·x), v(t) = v₀ / (1 + c·v₀·t)), and its
 * damage falls with the speed it keeps (core/bullets.ts, the speed-share rule). The sim's step and every predictor of a
 * round — the bots' aim and lead, the human's solve, dot and wind solve — read these, so they fly the same drag. Pure; the
 * exponentials through core/dmath.ts. With no drag (c = 0, the rocket's) each is today's no-drag formula.
 */
import { dexp, dlog } from './dmath';
import { AMMO_REFERENCE_PEOPLE, CARTRIDGES } from '../constants/ammo';
import type { CartridgeKind } from '../types';

const LN2 = 0.6931471805599453;

/** The drag constant c (per world unit) of a round, a person `person` world units tall. */
export function dragOf(round: CartridgeKind, person: number): number {
  return LN2 / (CARTRIDGES[round].halfSpeedPeople * person);
}

/** Each round's muzzle damage over its damage at AMMO_REFERENCE_PEOPLE: the speed the drag takes by then (once, at load). */
const MUZZLE_GAIN = Object.fromEntries((Object.keys(CARTRIDGES) as CartridgeKind[]).map(k => [k, dexp((LN2 * AMMO_REFERENCE_PEOPLE) / CARTRIDGES[k].halfSpeedPeople)])) as Record<CartridgeKind, number>;

/** A round's damage at the muzzle, from its damage at AMMO_REFERENCE_PEOPLE (`atReference`). */
export function muzzleDamage(round: CartridgeKind, atReference: number): number {
  return atReference * MUZZLE_GAIN[round];
}

/** The sim's drag over a step of `h` seconds at speed `speed`: the share of its speed a round keeps (exact for the step). */
export function dragKeep(speed: number, drag: number, h: number): number {
  return 1 / (1 + drag * speed * h);
}

/** Seconds for a round fired at `speed` to fly `d` (a flat shot). */
export function flightTime(speed: number, drag: number, d: number): number {
  return drag > 0 ? (dexp(drag * d) - 1) / (drag * speed) : d / speed;
}

/**
 * The drift a constant acceleration (gravity, the wind) gives a round in `t` seconds, per unit of it — ½·t² without drag.
 * Under the drag the across speed it gains bleeds too: (U² − 1 − 2·ln U) / (4·a²), U = 1 + a·t, a = c·v₀ (a flat shot).
 */
export function fallTime2(speed: number, drag: number, t: number): number {
  if (!(drag > 0)) return 0.5 * t * t;
  const a = drag * speed, e = a * t;
  if (e < 1e-4) return 0.5 * t * t * (1 - e / 3);   // (the series: the closed form cancels to nothing this small)
  const U = 1 + e;
  return (U * U - 1 - 2 * dlog(U)) / (4 * a * a);
}

/** How far a round flies in its life. */
export function roundReach(spec: { speed: number; life: number; drag?: number }): number {
  const c = spec.drag ?? 0;
  return c > 0 ? dlog(1 + c * spec.speed * spec.life) / c : spec.speed * spec.life;
}

/**
 * The speed whose no-drag fall over `d` is the dragged round's — what the closed-form pitch solve (aimSolve.ts
 * solveAimPitch) is given so its elevation answers the drag.
 */
export function dragAimSpeed(speed: number, drag: number, d: number): number {
  if (!(drag > 0) || !(d > 0)) return speed;
  return d / Math.sqrt(2 * fallTime2(speed, drag, flightTime(speed, drag, d)));
}
